import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Divider,
  Tabs,
  Tab
} from '@mui/material';
import { Delete as DeleteIcon, Close as CloseIcon, People as PeopleIcon } from '@mui/icons-material';
import cytoscape from 'cytoscape';

const relationshipTypes = [
  { value: 'friend', label: 'Friend', color: '#4CAF50' },
  { value: 'enemy', label: 'Enemy', color: '#F44336' },
  { value: 'family', label: 'Family', color: '#2196F3' },
  { value: 'lover', label: 'Lover', color: '#E91E63' },
  { value: 'mentor', label: 'Mentor', color: '#9C27B0' },
  { value: 'colleague', label: 'Colleague', color: '#FF9800' },
  { value: 'rival', label: 'Rival', color: '#795548' },
  { value: 'acquaintance', label: 'Acquaintance', color: '#607D8B' }
];

const RelationshipGraph = ({ 
  open, 
  onClose, 
  characters, 
  relationships, 
  onSaveRelationship, 
  onDeleteRelationship 
}) => {
  const [newRelationship, setNewRelationship] = useState({
    sourceId: '',
    targetId: '',
    type: '',
    description: ''
  });
  const [activeTab, setActiveTab] = useState(0);
  const [selectedNode, setSelectedNode] = useState(null);
  const graphRef = useRef(null);
  const cytoscapeRef = useRef(null);
  const [graphInitialized, setGraphInitialized] = useState(false);
  const [graphHeight, setGraphHeight] = useState(500);
  const isResizingRef = useRef(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(500);
  // Fixed default layout (force-directed)
  const layoutType = 'cose';
  const layoutTimerRef = useRef(null);
  const positionsRef = useRef({});
  const POSITIONS_KEY = 'relationshipGraphPositions';

  const hasSavedPositions = () => {
    const obj = positionsRef.current;
    return obj && Object.keys(obj).length > 0;
  };

  const loadSavedPositions = () => {
    try {
      const raw = localStorage.getItem(POSITIONS_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') return parsed;
    } catch {}
    return {};
  };

  const savePositions = () => {
    try {
      localStorage.setItem(POSITIONS_KEY, JSON.stringify(positionsRef.current || {}));
    } catch {}
  };

  const capturePositions = () => {
    if (!isCyReady()) return;
    const cy = cytoscapeRef.current;
    const next = { ...positionsRef.current };
    cy.nodes().forEach((n) => {
      next[n.id()] = n.position();
    });
    positionsRef.current = next;
    savePositions();
  };

  const stopAndDestroyCy = () => {
    capturePositions();
    if (layoutTimerRef.current) {
      clearTimeout(layoutTimerRef.current);
      layoutTimerRef.current = null;
    }
    const cy = cytoscapeRef.current;
    if (cy && !cy.destroyed()) {
      try { cy.stop(); } catch {}
      try { cy.destroy(); } catch {}
    }
    cytoscapeRef.current = null;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewRelationship(prev => ({ ...prev, [name]: value }));
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    
    // When switching to graph tab, reinitialize to avoid stale containers
    if (newValue === 1) {
      stopAndDestroyCy();
      setGraphInitialized(false);
      setTimeout(() => {
        initializeGraph();
      }, 300);
    }
  };

  // Also react when activeTab changes to graph: ensure we have a fresh graph
  useEffect(() => {
    if (!open) return;
    if (activeTab !== 1) return;
    // Always (re)initialize upon tab mount
    initializeGraph();
  }, [activeTab, open, runLayout]);

  // Load saved positions on mount
  useEffect(() => {
    positionsRef.current = loadSavedPositions();
  }, []);

  // Drag-to-resize handlers for graph height
  const handleGraphResizeStart = (e) => {
    isResizingRef.current = true;
    startYRef.current = e.clientY ?? (e.touches && e.touches[0]?.clientY) ?? 0;
    startHeightRef.current = graphHeight;
    window.addEventListener('mousemove', handleGraphResizing);
    window.addEventListener('mouseup', handleGraphResizeEnd);
    window.addEventListener('touchmove', handleGraphResizing, { passive: false });
    window.addEventListener('touchend', handleGraphResizeEnd);
  };

  const handleGraphResizing = (e) => {
    if (!isResizingRef.current) return;
    if (!isCyReady()) return;
    const currentY = e.clientY ?? (e.touches && e.touches[0]?.clientY) ?? 0;
    const delta = currentY - startYRef.current;
    const newHeight = Math.max(320, Math.min(1000, startHeightRef.current + delta));
    setGraphHeight(newHeight);
    if (isCyReady()) {
      try {
        cytoscapeRef.current.resize();
      } catch {}
    }
    // prevent scroll while resizing on touch
    if (e.cancelable) e.preventDefault();
  };

  const handleGraphResizeEnd = () => {
    if (!isResizingRef.current) return;
    isResizingRef.current = false;
    window.removeEventListener('mousemove', handleGraphResizing);
    window.removeEventListener('mouseup', handleGraphResizeEnd);
    window.removeEventListener('touchmove', handleGraphResizing);
    window.removeEventListener('touchend', handleGraphResizeEnd);
    if (cytoscapeRef.current) {
      try {
        // Optionally refit lightly after resize for visibility
        cytoscapeRef.current.resize();
      } catch {}
    }
  };

  // Export graph as PNG
  const handleExportPng = () => {
    if (!cytoscapeRef.current) return;
    try {
      const pngData = cytoscapeRef.current.png({ full: true, scale: 2, bg: '#ffffff' });
      const link = document.createElement('a');
      link.href = pngData;
      link.download = 'character-relationships.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error('Error exporting graph PNG:', e);
    }
  };

  // Helper functions for node styling
  const getInitials = (name) => {
    if (!name) return '';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase();
  };

  const getColorFromName = (name) => {
    if (!name) return '#607d8b';
    
    // Generate a color based on the name
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    // Convert to a color
    let color = '#';
    for (let i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 0xFF;
      color += ('00' + value.toString(16)).substr(-2);
    }
    
    return color;
  };

  // ID normalizer
  const toId = (v) => (v !== undefined && v !== null) ? String(v) : '';

  const isCyReady = () => cytoscapeRef.current && !cytoscapeRef.current.destroyed();

  // Run layout (force-directed)
  function runLayout() {
    // If positions are already saved, skip force layout to avoid jump
    if (hasSavedPositions()) return;
    if (!isCyReady()) return;
    const cy = cytoscapeRef.current;
    if (!cy || cy.destroyed()) return;
    if (cy.elements().length === 0) return;
    try {
      const layoutConfig = {
        name: 'cose',
        animate: true,
        animationDuration: 800,
        refresh: 20,
        fit: true,
        padding: 50,
        nodeDimensionsIncludeLabels: true,
        randomize: false,
        componentSpacing: 100,
        nodeRepulsion: 5000,
        nodeOverlap: 20,
        idealEdgeLength: 120,
        edgeElasticity: 120,
        nestingFactor: 5,
        gravity: 80,
        initialTemp: 150,
        coolingFactor: 0.95
      };
      const layout = cytoscapeRef.current.layout(layoutConfig);
      layout.run();
      // Capture positions after layout completes
      layout.once('layoutstop', () => {
        capturePositions();
      });
    } catch (error) {
      console.error('Error running layout:', error);
    }
  }

  // Function to initialize the graph
  const initializeGraph = useCallback(() => {
    if (!graphRef.current || !open || activeTab !== 1) return;
    
    try {
      // Clean up existing instance
      if (cytoscapeRef.current) {
        try {
          cytoscapeRef.current.destroy();
        } catch (e) {
          console.error("Error destroying previous Cytoscape instance:", e);
        }
        cytoscapeRef.current = null;
      }

      // Create elements for Cytoscape
      const elements = [];
      
      // Only proceed if we have characters and relationships
      if (!characters || !relationships || characters.length === 0 || relationships.length === 0) {
        console.log("No characters or relationships to display");
        return;
      }
      
      // Add characters as nodes
      const characterMap = {};
      characters.forEach(character => {
        characterMap[toId(character.id)] = character;
      });

      const savedPositions = positionsRef.current || {};
      
      // First check which characters are in relationships
      const charactersInRelationships = new Set();
      relationships.forEach(rel => {
        if (rel.sourceId && rel.targetId) {
          charactersInRelationships.add(rel.sourceId);
          charactersInRelationships.add(rel.targetId);
        }
      });
      
      // Calculate container dimensions
      const containerWidth = graphRef.current.offsetWidth || 600;
      const containerHeight = graphRef.current.offsetHeight || 400;
      const centerX = containerWidth / 2;
      const centerY = containerHeight / 2;
      
      // Calculate positions in a circle
      const charactersArray = Array.from(charactersInRelationships).map(toId);
      const radius = Math.min(containerWidth, containerHeight) * 0.35;
      
      // Add only characters that are in relationships
      charactersArray.forEach((characterId, index) => {
        const character = characterMap[toId(characterId)];
        if (character) {
          const initials = getInitials(character.name);
          const bgColor = character.imageUrl ? '#f5f5f5' : getColorFromName(character.name);
          
          // Use saved position if available, else circle placement
          const saved = savedPositions[toId(character.id)];
          const angle = (2 * Math.PI * index) / charactersArray.length;
          const fallback = {
            x: centerX + radius * Math.cos(angle),
            y: centerY + radius * Math.sin(angle)
          };
          const pos = saved && typeof saved.x === 'number' && typeof saved.y === 'number'
            ? saved
            : fallback;
          
          elements.push({
            data: { 
              id: toId(character.id), 
              label: character.name,
              role: character.role || 'unknown',
              imageUrl: character.imageUrl || null,
              initials: initials,
              bgColor: bgColor
            },
            position: pos
          });
        }
      });
      
      // Add relationships as edges
      relationships.forEach((rel, i) => {
        if (rel.sourceId && rel.targetId) {
          // Verify that both source and target nodes exist
          const sourceExists = characterMap[toId(rel.sourceId)] !== undefined;
          const targetExists = characterMap[toId(rel.targetId)] !== undefined;
          
          if (sourceExists && targetExists) {
            const relType = relationshipTypes.find(t => t.value === rel.type);
            elements.push({
              data: {
                id: rel.id ? `edge-${toId(rel.id)}` : `edge-${toId(rel.sourceId)}-${toId(rel.targetId)}-${rel.type}-${i}`,
                source: toId(rel.sourceId),
                target: toId(rel.targetId),
                label: relType ? relType.label : rel.type,
                type: rel.type,
                color: relType ? relType.color : '#9E9E9E',
                description: rel.description
              }
            });
          }
        }
      });
      
      // Only initialize Cytoscape if we have elements
      if (elements.length > 0) {
        console.log("Initializing Cytoscape with", elements.length, "elements");
        
        // Initialize Cytoscape
        cytoscapeRef.current = cytoscape({
          container: graphRef.current,
          elements: elements,
          wheelSensitivity: 0.2,
          minZoom: 0.2,
          maxZoom: 3,
          style: [
            {
              selector: 'node',
              style: {
                'background-color': 'data(bgColor)',
                'border-width': 2,
                'border-color': '#1976d2',
                'label': function(ele) {
                  return ele.data('imageUrl') ? ele.data('label') : '';
                },
                'text-valign': 'bottom',
                'text-margin-y': 5,
                'text-halign': 'center',
                'font-size': '12px',
                'width': '60px',
                'height': '60px',
                'text-wrap': 'wrap',
                'text-max-width': '80px',
                'background-fit': 'cover',
                'background-image': 'data(imageUrl)',
                'content': function(ele) {
                  return ele.data('imageUrl') ? '' : ele.data('label');
                },
                'text-opacity': function(ele) {
                  return ele.data('imageUrl') ? 0 : 1;
                },
                'color': 'white',
                'text-outline-width': 1,
                'text-outline-color': 'rgba(0,0,0,0.5)',
                'font-weight': 'bold',
                'font-size': '10px',
                'text-wrap': 'wrap',
                'text-max-width': '56px',
                'text-valign': 'center'
              }
            },
            {
              selector: 'node[role = "protagonist"]',
              style: {
                'background-color': function(ele) {
                  return ele.data('imageUrl') ? '#bbdefb' : '#1976d2';
                },
                'border-width': 3,
                'border-color': '#1976d2',
                'width': '70px',
                'height': '70px',
                'font-weight': 'bold',
                'background-fit': 'cover',
                'background-image': 'data(imageUrl)',
                'content': function(ele) {
                  return ele.data('imageUrl') ? '' : ele.data('label');
                },
                'color': 'white',
                'text-opacity': function(ele) {
                  return ele.data('imageUrl') ? 0 : 1;
                },
                'text-wrap': 'wrap',
                'text-max-width': '66px',
                'text-valign': 'center',
                'text-outline-width': 1,
                'text-outline-color': 'rgba(0,0,0,0.5)'
              }
            },
            {
              selector: 'node[role = "antagonist"]',
              style: {
                'background-color': function(ele) {
                  return ele.data('imageUrl') ? '#ffcdd2' : '#d32f2f';
                },
                'border-width': 3,
                'border-color': '#d32f2f',
                'width': '70px',
                'height': '70px',
                'font-weight': 'bold',
                'background-fit': 'cover',
                'background-image': 'data(imageUrl)',
                'content': function(ele) {
                  return ele.data('imageUrl') ? '' : ele.data('label');
                },
                'color': 'white',
                'text-opacity': function(ele) {
                  return ele.data('imageUrl') ? 0 : 1;
                },
                'text-wrap': 'wrap',
                'text-max-width': '66px',
                'text-valign': 'center',
                'text-outline-width': 1,
                'text-outline-color': 'rgba(0,0,0,0.5)'
              }
            },
            {
              selector: 'edge',
              style: {
                'width': 3,
                'line-color': 'data(color)',
                'target-arrow-color': 'data(color)',
                'target-arrow-shape': 'triangle',
                'curve-style': 'bezier',
                'label': 'data(label)',
                'font-size': '10px',
                'text-rotation': 'autorotate',
                'text-background-color': 'white',
                'text-background-opacity': 0.7,
                'text-background-padding': '2px'
              }
            }
          ]
        });
        
          // Fit the graph after a small delay, then run default layout
        // Fit once; skip force layout if positions are saved to avoid jump
        setTimeout(() => {
          if (cytoscapeRef.current) {
            cytoscapeRef.current.fit(undefined, 50); // 50px padding
            if (!hasSavedPositions()) {
              layoutTimerRef.current = setTimeout(() => {
                if (isCyReady()) {
                  runLayout();
                }
              }, 200);
            }
          }
        }, 100);
        
        // Add interactivity
        cytoscapeRef.current.on('tap', 'node', function(evt) {
          const node = evt.target;
          const character = characters.find(c => c.id === node.id());
          setSelectedNode(character);
        });

        // Persist positions on drag
        cytoscapeRef.current.on('dragfree', 'node', () => {
          capturePositions();
        });
        // Persist after layout runs
        cytoscapeRef.current.on('layoutstop', () => {
          capturePositions();
        });
        
        cytoscapeRef.current.on('tap', function(evt) {
          if (evt.target === cytoscapeRef.current) {
            // Clicked on background
            setSelectedNode(null);
          }
        });
        
        // Add panning cursor
        cytoscapeRef.current.on('mousedown', function() {
          if (graphRef.current) {
            graphRef.current.style.cursor = 'grabbing';
          }
        });
        
        cytoscapeRef.current.on('mouseup', function() {
          if (graphRef.current) {
            graphRef.current.style.cursor = 'grab';
          }
        });
        
        // Set initial cursor
        if (graphRef.current) {
          graphRef.current.style.cursor = 'grab';
        }
        
        setGraphInitialized(true);
      }
    } catch (error) {
      console.error("Error initializing Cytoscape:", error);
    }
  }, [characters, relationships, activeTab, open, runLayout]);

  // Initialize graph when dialog opens or tab changes
  useEffect(() => {
    let initTimer = null;
    
    if (open && activeTab === 1 && !graphInitialized) {
      // Try initializing after a delay
      initTimer = setTimeout(() => {
        initializeGraph();
      }, 500);
    }
    
    return () => {
      if (initTimer) {
        clearTimeout(initTimer);
      }
    };
  }, [open, activeTab, graphInitialized, initializeGraph]);

  // Live-update graph when characters/relationships change while graph is visible
  useEffect(() => {
    if (!open || activeTab !== 1) return;
    if (!graphInitialized) return;
    if (!isCyReady()) return;

    try {
      // Build fresh elements using the same logic as initializeGraph
      const elements = [];
      if (!characters || !relationships || characters.length === 0 || relationships.length === 0) {
        // If emptied, just clear
        if (isCyReady()) {
          try {
            cytoscapeRef.current.elements().remove();
          } catch {}
        }
        return;
      }

      const characterMap = {};
      characters.forEach(c => { characterMap[toId(c.id)] = c; });

      const charactersInRelationships = new Set();
      relationships.forEach(rel => {
        if (rel.sourceId && rel.targetId) {
          charactersInRelationships.add(rel.sourceId);
          charactersInRelationships.add(rel.targetId);
        }
      });

      // Use current viewport size
      const container = graphRef.current;
      const containerWidth = container?.offsetWidth || 600;
      const containerHeight = container?.offsetHeight || 400;
      const centerX = containerWidth / 2;
      const centerY = containerHeight / 2;
      const charactersArray = Array.from(charactersInRelationships);
      const radius = Math.min(containerWidth, containerHeight) * 0.35;

      charactersArray.forEach((characterId, index) => {
        const character = characterMap[characterId];
        if (character) {
          const initials = getInitials(character.name);
          const bgColor = character.imageUrl ? '#f5f5f5' : getColorFromName(character.name);
          const angle = (2 * Math.PI * index) / charactersArray.length;
          const x = centerX + radius * Math.cos(angle);
          const y = centerY + radius * Math.sin(angle);
          elements.push({
            data: {
              id: toId(character.id),
              label: character.name,
              role: character.role || 'unknown',
              imageUrl: character.imageUrl || null,
              initials,
              bgColor
            },
            position: { x, y }
          });
        }
      });

      relationships.forEach((rel, i) => {
        if (rel.sourceId && rel.targetId && characterMap[toId(rel.sourceId)] && characterMap[toId(rel.targetId)]) {
          const relType = relationshipTypes.find(t => t.value === rel.type);
          elements.push({
            data: {
              id: rel.id ? `edge-${toId(rel.id)}` : `edge-${toId(rel.sourceId)}-${toId(rel.targetId)}-${rel.type}-${i}`,
              source: toId(rel.sourceId),
              target: toId(rel.targetId),
              label: relType ? relType.label : rel.type,
              type: rel.type,
              color: relType ? relType.color : '#9E9E9E',
              description: rel.description
            }
          });
        }
      });

      // Replace elements in place
      if (!isCyReady()) return;
      try {
        cytoscapeRef.current.elements().remove();
        cytoscapeRef.current.add(elements);
      } catch (err) {
        console.error('Error updating Cytoscape elements (batch):', err);
        return;
      }
      console.log('[Relationships Graph] Live update: nodes', elements.filter(e => e.data && !e.data.source).length, 'edges', elements.filter(e => e.data && e.data.source).length);
      // Re-run default layout
      try {
        runLayout();
      } catch (err) {
        console.error('Error running layout after update:', err);
      }
    } catch (e) {
      console.error('Error updating Cytoscape elements:', e);
    }
  }, [characters, relationships, open, activeTab, runLayout]);
  
  // Clean up Cytoscape instance when component unmounts or dialog closes
  useEffect(() => {
    if (!open) {
      stopAndDestroyCy();
      setGraphInitialized(false);
    }
    return () => {
      stopAndDestroyCy();
      setGraphInitialized(false);
    };
  }, [open]);

  const handleSave = () => {
    if (!newRelationship.sourceId || !newRelationship.targetId || !newRelationship.type) {
      return;
    }
    onSaveRelationship(newRelationship);
    setNewRelationship({
      sourceId: '',
      targetId: '',
      type: '',
      description: ''
    });
  };

  const handleDelete = (id) => {
    onDeleteRelationship(id);
  };

  const getCharacterName = (id) => {
    const character = characters.find(c => c.id === id);
    return character ? character.name : 'Unknown Character';
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      aria-labelledby="relationship-dialog-title"
      TransitionProps={{
        onEntered: () => {
          // When dialog is fully open, try to initialize graph
          if (relationships && relationships.length > 0) {
            setActiveTab(1);
          }
        }
      }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 10px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle 
        id="relationship-dialog-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              background: `linear-gradient(135deg, #9C27B0, #7B1FA2)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 4px 12px rgba(156,39,176,0.3)`
            }}
          >
            <PeopleIcon sx={{ color: 'white', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              Character Relationships
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
              Visualize connections between characters
            </Typography>
          </Box>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: (theme) => theme.palette.text.secondary }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="relationship tabs">
            <Tab label="Manage Relationships" />
            <Tab label="Relationship Graph" />
          </Tabs>
        </Box>
        
        {activeTab === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>Add New Relationship</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="source-character-label">Source Character</InputLabel>
                <Select
                  labelId="source-character-label"
                  name="sourceId"
                  value={newRelationship.sourceId}
                  label="Source Character"
                  onChange={handleChange}
                >
                  {characters.map((character) => (
                    <MenuItem key={character.id} value={character.id}>{character.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="target-character-label">Target Character</InputLabel>
                <Select
                  labelId="target-character-label"
                  name="targetId"
                  value={newRelationship.targetId}
                  label="Target Character"
                  onChange={handleChange}
                >
                  {characters.map((character) => (
                    <MenuItem 
                      key={character.id} 
                      value={character.id}
                      disabled={character.id === newRelationship.sourceId}
                    >
                      {character.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="relationship-type-label">Relationship Type</InputLabel>
                <Select
                  labelId="relationship-type-label"
                  name="type"
                  value={newRelationship.type}
                  label="Relationship Type"
                  onChange={handleChange}
                >
                  {relationshipTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box 
                          sx={{ 
                            width: 16, 
                            height: 16, 
                            borderRadius: '50%', 
                            bgcolor: type.color,
                            mr: 1 
                          }} 
                        />
                        {type.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="description"
                label="Relationship Description"
                fullWidth
                multiline
                rows={3}
                value={newRelationship.description}
                onChange={handleChange}
                placeholder="Describe the relationship between these characters..."
              />
            </Grid>
            <Grid item xs={12}>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleSave}
                disabled={!newRelationship.sourceId || !newRelationship.targetId || !newRelationship.type}
              >
                Add Relationship
              </Button>
            </Grid>
            
            <Grid item xs={12} sx={{ mt: 4 }}>
              <Typography variant="subtitle1" gutterBottom>Existing Relationships</Typography>
              {relationships.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No relationships defined yet.
                </Typography>
              ) : (
                <List>
                  {relationships.map((rel, index) => (
                    <ListItem key={rel.id || `${rel.sourceId}-${rel.targetId}-${rel.type}-${index}`} divider>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box 
                              sx={{ 
                                width: 12, 
                                height: 12, 
                                borderRadius: '50%', 
                                bgcolor: relationshipTypes.find(t => t.value === rel.type)?.color || '#9E9E9E',
                                mr: 1 
                              }} 
                            />
                            <Typography variant="body1">
                              {getCharacterName(rel.sourceId)} → {getCharacterName(rel.targetId)}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography component="span" variant="body2" color="text.primary">
                              {relationshipTypes.find(t => t.value === rel.type)?.label || rel.type}
                            </Typography>
                            {rel.description && (
                              <Typography component="span" variant="body2" display="block">
                                {rel.description}
                              </Typography>
                            )}
                          </>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton edge="end" aria-label="delete" onClick={() => onDeleteRelationship(rel.id || `${rel.sourceId}-${rel.targetId}-${rel.type}`)}>
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Grid>
          </Grid>
        )}
        
        {activeTab === 1 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={selectedNode ? 8 : 12}>
              <Paper sx={{ 
                p: 1.5, 
                mb: 2, 
                display: 'flex', 
                alignItems: 'center',
                borderRadius: 2,
                border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                background: (theme) => theme.palette.mode === 'dark'
                  ? 'linear-gradient(145deg, rgba(255,255,255,0.03), rgba(255,255,255,0.015))'
                  : 'linear-gradient(145deg, #fff, #fafafa)'
              }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mr: 1 }}>Graph tools</Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Button 
                  size="small" 
                  sx={{ ml: 2 }}
                  onClick={() => {
                    if (isCyReady()) {
                      cytoscapeRef.current.fit(undefined, 50);
                      setTimeout(() => runLayout(), 200);
                    }
                  }}
                >
                  Recenter
                </Button>
                <Button size="small" variant="outlined" sx={{ ml: 1 }} onClick={handleExportPng}>
                  Export PNG
                </Button>
              </Paper>
              {/* Resizer bar */}
              <Box
                onMouseDown={handleGraphResizeStart}
                onTouchStart={handleGraphResizeStart}
                sx={{
                  mt: 0.5,
                  height: 10,
                  cursor: 'row-resize',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  userSelect: 'none'
                }}
              >
                <Paper elevation={0} sx={{ width: 80, height: 4, borderRadius: 2, bgcolor: 'divider' }} />
              </Box>
              <Paper 
                ref={graphRef} 
                sx={{ 
                  height: graphHeight, 
                  width: '100%', 
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8f9fa',
                  borderRadius: 2,
                  border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {!relationships || relationships.length === 0 ? (
                  <Box sx={{ textAlign: 'center', p: 3 }}>
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                      No relationships defined yet.
                    </Typography>
                    <Button 
                      variant="outlined" 
                      onClick={() => setActiveTab(0)}
                    >
                      Add Relationships
                    </Button>
                  </Box>
                ) : null}
              </Paper>
              <Paper elevation={0} sx={{ p: 1, mt: 1.5, borderRadius: 2, border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}` }}>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>Legend:</Typography>
                {relationshipTypes.map((type) => (
                  <Box key={type.value} component="span" sx={{ display: 'inline-flex', alignItems: 'center', mr: 1.5, mb: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: type.color, mr: 0.5 }} />
                    <Typography variant="caption">{type.label}</Typography>
                  </Box>
                ))}
              </Paper>
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', textAlign: 'center' }}>
                Tip: Drag to pan, scroll to zoom, click on nodes for details
              </Typography>
            </Grid>
            
            {selectedNode && (
              <Grid item xs={12} sm={4}>
                <Paper sx={{ 
                  p: 2, 
                  height: '100%',
                  borderRadius: 2,
                  border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`
                }}>
                  <Typography variant="h6" gutterBottom>
                    {selectedNode.name}
                  </Typography>
                  
                  {selectedNode.imageUrl && (
                    <Box 
                      component="img" 
                      src={selectedNode.imageUrl} 
                      alt={selectedNode.name}
                      sx={{ 
                        width: '100%', 
                        maxHeight: 200, 
                        objectFit: 'cover',
                        borderRadius: 1,
                        mb: 2
                      }}
                    />
                  )}
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Role: {selectedNode.role || 'Not specified'}
                  </Typography>
                  
                  {selectedNode.archetype && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Archetype: {selectedNode.archetype}
                    </Typography>
                  )}
                  
                  <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                    Relationships:
                  </Typography>
                  
                  <List dense>
                    {relationships
                      .filter(rel => rel.sourceId === selectedNode.id || rel.targetId === selectedNode.id)
                      .map(rel => {
                        const isSource = rel.sourceId === selectedNode.id;
                        const otherCharacterId = isSource ? rel.targetId : rel.sourceId;
                        const otherCharacter = characters.find(c => c.id === otherCharacterId);
                        const relType = relationshipTypes.find(t => t.value === rel.type);
                        
                        return (
                          <ListItem key={rel.id} sx={{ px: 1 }}>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Box 
                                    sx={{ 
                                      width: 10, 
                                      height: 10, 
                                      borderRadius: '50%', 
                                      bgcolor: relType?.color || '#9E9E9E',
                                      mr: 1 
                                    }} 
                                  />
                                  <Typography variant="body2">
                                    {isSource ? 'Is' : 'Has'} {relType?.label || rel.type} {isSource ? 'to' : 'from'} {otherCharacter?.name || 'Unknown Character'}
                                  </Typography>
                                </Box>
                              }
                              secondary={rel.description ? <Typography variant="caption">{rel.description}</Typography> : null}
                            />
                          </ListItem>
                        );
                      })}
                  </List>
                </Paper>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RelationshipGraph;
